import { createSlice } from '@reduxjs/toolkit';
import { InstructorSampleState, SchoolAddress, SchoolAddressMagentoResponse } from './instructor-sample.interface';
import { createSampleOrderProductInfo, getMagentoSchoolAddressThunk } from './instructor-sample.thunks';
import { defaultApiState } from '../../common/constants/api-constants';
import { ActionStatus } from '../../common/constants/action-status';
import { RECAPTCHA_HEADER, getMagentoSchoolAddressUrl } from '../../common/constants/shop-api';
import { getResponseData } from '../../common/utils/api-utils';
import environment from '../../common/environment';
import { instructorSampleOrderThunk } from './instructor-sample-magento.thunks';
import { mapSchoolAddressMagento } from '../address/mapping/school-address.mapping';
import { getCustomerToken } from '../customer/customer';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';

/*----- CREATE INITIAL STATE ------*/


export const instructorSampleStateInitialState: InstructorSampleState = {
  getSchoolAddress: defaultApiState,
  getInstructorData: defaultApiState,
  getSampleOrderProductInfo: defaultApiState,
  getInstructorSampleOrder: defaultApiState,
};

/*----- CREATE STORE SLICE ------*/
export const instructorSampleStatusSlice = createSlice({
  name: 'instructorSample',
  initialState: instructorSampleStateInitialState,
  reducers: {
    updateInstructorSampleStatus(state, action) {
      state.getSchoolAddress.status = ActionStatus[action.payload];
    },
    updateInstructorData(state,action) {
      state.getInstructorData.data = action.payload;
    },
    updateSampleOrderProductInfo(state,action) {
      state.getSampleOrderProductInfo.data = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getMagentoSchoolAddressThunk.pending, (state) => {
        state.getSchoolAddress.status = ActionStatus.loading;
      })
      .addCase(getMagentoSchoolAddressThunk.fulfilled, (state, action) => {
        state.getSchoolAddress.status = ActionStatus.succeeded;
        state.getSchoolAddress.data = action.payload;
        state.getSchoolAddress.error = undefined;
      })
      .addCase(getMagentoSchoolAddressThunk.rejected, (state, action: any) => {
        state.getSchoolAddress.status = ActionStatus.failed;
        console.error(action);
        state.getSchoolAddress.error = action.payload?.error;
        state.getSchoolAddress.data = [];
      })
      .addCase(createSampleOrderProductInfo.pending, (state) => {
        state.getSampleOrderProductInfo.status = ActionStatus.loading;
      })
      .addCase(createSampleOrderProductInfo.fulfilled, (state, action) => {
        state.getSampleOrderProductInfo.status = ActionStatus.succeeded;
        state.getSampleOrderProductInfo.data = action.payload;
        state.getSampleOrderProductInfo.error = undefined;
      })
      .addCase(createSampleOrderProductInfo.rejected, (state, action: any) => {
        state.getSampleOrderProductInfo.status = ActionStatus.failed;
        state.getSampleOrderProductInfo.error = action.payload?.error;
      })
      .addCase(instructorSampleOrderThunk.pending, (state) => {
        state.getInstructorSampleOrder.status = ActionStatus.loading;
        state.getInstructorSampleOrder.error = undefined;
      })
      .addCase(instructorSampleOrderThunk.fulfilled, (state, action) => {
        state.getInstructorSampleOrder.status = ActionStatus.succeeded;
        state.getInstructorSampleOrder.data = action.payload;
        state.getInstructorSampleOrder.error = undefined;
      })
      .addCase(instructorSampleOrderThunk.rejected, (state, action: any) => {
        state.getInstructorSampleOrder.status = ActionStatus.failed;
        state.getInstructorSampleOrder.error = action.payload;
})
  }
});
/*----- CREATE SLICE ACTIONS ------*/

export const { updateInstructorSampleStatus,updateInstructorData, updateSampleOrderProductInfo} = instructorSampleStatusSlice.actions;
/*----- CREATE REDUCER ------*/

export const instructorSampleReducer = instructorSampleStatusSlice.reducer;

/*----- API CALLS ------*/

export async function getSchoolAddressMagentoApiCall( recaptchaToken?: string): Promise<SchoolAddress[]>  {
  const url = new URL(getMagentoSchoolAddressUrl());
  const customerToken = getCustomerToken(store.getState() as StoreState);
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${customerToken}`
  }
  if (recaptchaToken) {
    headers[RECAPTCHA_HEADER] = recaptchaToken;
  }
  const response: SchoolAddressMagentoResponse = await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers
    })
  );

  return mapSchoolAddressMagento(response);
}

export async function getSampleOrderProductInfo(isbn: string) {
  const url = new URL(`${environment().searchServiceUrl}/product`);
  const input = { mhid: isbn, businessUnit: "HE"};
  const headers = {
    'Content-Type': 'application/json'
  }
  
  return await getResponseData( 
    new Request(url.toString(),{
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(input)
    }));
}
